import { createPatchData, useRolodexEdit } from 'features/fde'
import {
  LIBRA_WORKFLOW_ENTRY_TYPE,
  type LibraWorkflow,
  mapEntryToLibraWorkflow,
} from 'features/libra/workflow/workflowStepsApi'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoNotifications } from 'features/notifications'
import {
  generateEntrySlug,
  type RolodexEntryQueryBody,
  useRolodexEntries,
} from 'features/rolodex'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { useCallback, useMemo } from 'react'
import {
  GeckoEntry,
  RolodexEntryPatchJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'
import { v4 } from 'uuid'
import z from 'zod'

export const LIBRA_TICKET_ENTRY_TYPE = 'libra-ticket' as RolodexEntryType
export const LIBRA_TICKET_GROUP_ENTRY_TYPE =
  'libra-ticket-group' as RolodexEntryType

const libraTicketGroupDataSchema = z.object({
  title: z.string().default('Untitled Group'),
})
type LibraTicketGroupData = z.infer<typeof libraTicketGroupDataSchema>

export type LibraTicketGroup = LibraTicketGroupData & {
  id: string
  tags: RolodexEntryTagsType
}

export interface LibraTicket {
  id: string
  workflowId: string
  title: string
  fieldValues: Record<string, unknown>
  stepId: string
  workflowComplete: boolean
  createdAt: string
  updatedAt: string
  tags: RolodexEntryTagsType
}

const createLibraTicketDataSchema = (defaultStepId: string) =>
  z.object({
    title: z.string(),
    fieldValues: z.record(z.string(), z.unknown()).default({}),
    stepId: z.string().default(defaultStepId),
    workflowComplete: z.boolean().optional().default(false),
  })

const mapEntryToLibraTicket = (
  entry: GeckoEntry,
  parseTicket: (data: GeckoEntry['data']) => {
    title: string
    fieldValues: Record<string, unknown>
    stepId: string
    workflowComplete: boolean
  }
): LibraTicket | undefined => {
  const workflowId = entry.tags[LIBRA_WORKFLOW_ENTRY_TYPE]
  if (!workflowId) {
    return undefined
  }

  const parsedData = parseTicket({ ...entry.data })

  return {
    id: entry.id,
    title: parsedData.title,
    fieldValues: parsedData.fieldValues,
    stepId: parsedData.stepId,
    workflowComplete: parsedData.workflowComplete,
    createdAt: entry.created_at ?? '',
    updatedAt: entry.updated_at ?? '',
    workflowId,
    tags: entry.tags,
  }
}

const mapEntryToLibraTicketGroup = (
  entry: GeckoEntry,
  parseGroup: (data: GeckoEntry['data']) => {
    title: string
  }
): LibraTicketGroup => {
  const parsedData = parseGroup({ ...entry.data })

  return {
    id: entry.id,
    title: parsedData.title,
    tags: entry.tags,
  }
}

export type UpdateTicketArgs = {
  ticketId: string
  stepId?: string
  fieldValues?: Record<string, unknown>
  workflowComplete?: boolean
}

type CreateTicketArgs = {
  title: string
  organization: string
  site: string
  unit: string
  groupId?: string
}

type CreateGroupArgs = {
  title: string
  organization?: string | null
  site?: string | null
  unit?: string | null
}

export function useCreateTicket(): (args: CreateTicketArgs) => Promise<string> {
  const { rolodexPut } = useRolodexEdit()
  const workflow = useWorkflow()
  return useCallback(
    async ({ title, organization, site, unit, groupId }: CreateTicketArgs) => {
      const schema = createLibraTicketDataSchema(
        first(workflow.steps)?.id ?? ''
      )
      const ticket = schema.parse({
        title,
        fieldValues: {},
        workflowComplete: false,
      })
      const id = generateEntrySlug('ticket', v4().slice(0, 6))
      await rolodexPut([
        {
          id,
          type: LIBRA_TICKET_ENTRY_TYPE,
          data: ticket,
          tags: {
            organization,
            site,
            unit,
            [LIBRA_WORKFLOW_ENTRY_TYPE]: workflow.id,
            [LIBRA_TICKET_GROUP_ENTRY_TYPE]: groupId,
          },
        },
      ])
      return id
    },
    [rolodexPut, workflow]
  )
}

export function useCreateGroup(): (args: CreateGroupArgs) => Promise<string> {
  const { rolodexPut } = useRolodexEdit()
  const workflow = useWorkflow()
  return useCallback(
    async ({ title, organization, site }: CreateGroupArgs) => {
      const group = libraTicketGroupDataSchema.parse({
        title,
      })
      const id = generateEntrySlug('ticket-group', v4().slice(0, 6))
      const tags: RolodexEntryTagsType = {
        [LIBRA_WORKFLOW_ENTRY_TYPE]: workflow.id,
      }

      if (organization) {
        tags.organization = organization
      }
      if (site) {
        tags.site = site
      }

      await rolodexPut([
        {
          id,
          type: LIBRA_TICKET_GROUP_ENTRY_TYPE,
          data: group,
          tags,
        },
      ])
      return id
    },
    [rolodexPut, workflow]
  )
}

// Dashboard version - creates group without workflow context
export function useCreateGroupForDashboard(): (
  args: CreateGroupArgs
) => Promise<string> {
  const { rolodexPut } = useRolodexEdit()
  return useCallback(
    async ({ title, organization, site }: CreateGroupArgs) => {
      const group = libraTicketGroupDataSchema.parse({
        title,
      })
      const id = generateEntrySlug('ticket-group', v4().slice(0, 6))
      const tags: RolodexEntryTagsType = {}

      if (organization) {
        tags.organization = organization
      }
      if (site) {
        tags.site = site
      }

      await rolodexPut([
        {
          id,
          type: LIBRA_TICKET_GROUP_ENTRY_TYPE,
          data: group,
          tags,
        },
      ])
      return id
    },
    [rolodexPut]
  )
}

export const useLibraTickets = (
  tags: RolodexEntryTagsType
): {
  tickets: ReadonlyArray<LibraTicket>
  groups: ReadonlyArray<LibraTicketGroup>
  ticketGroups: ReadonlyMap<string, LibraTicketGroup>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => Promise<void>
  doUpdateTicket: (arg: UpdateTicketArgs) => Promise<void>
} => {
  const queryBody = useMemo(() => {
    return {
      type: LIBRA_TICKET_ENTRY_TYPE,
      links: [LIBRA_TICKET_GROUP_ENTRY_TYPE, LIBRA_WORKFLOW_ENTRY_TYPE],
      tags,
    }
  }, [tags])

  const { entries, isLoading, hasEverLoaded, forceRefresh } =
    useRolodexEntries(queryBody)

  const workflowIdToDefaultStepId = useMemo<ReadonlyMap<string, string>>(() => {
    const workflows = (entries ?? [])
      .filter((entry) => entry.type === LIBRA_WORKFLOW_ENTRY_TYPE)
      .map(mapEntryToLibraWorkflow)

    return workflows.reduce((acc, workflow) => {
      const firstStep = first(workflow.steps)
      return firstStep ? new Map(acc).set(workflow.id, firstStep.id) : acc
    }, new Map<string, string>())
  }, [entries])

  const { ticketEntries, groupEntries } = useMemo(
    () => ({
      ticketEntries: (entries ?? []).filter(
        (entry) => entry.type === LIBRA_TICKET_ENTRY_TYPE
      ),
      groupEntries: (entries ?? []).filter(
        (entry) => entry.type === LIBRA_TICKET_GROUP_ENTRY_TYPE
      ),
    }),
    [entries]
  )
  const { errorNotification } = useGeckoNotifications()
  const { rolodexPatch } = useRolodexEdit()

  const getTicketSchema = useCallback(
    (ticket: GeckoEntry) => {
      const workflowId = ticket.tags[LIBRA_WORKFLOW_ENTRY_TYPE]
      const result = workflowId
        ? createLibraTicketDataSchema(
            workflowIdToDefaultStepId.get(workflowId) ?? ''
          )
        : undefined
      if (!result) {
        errorNotification(
          `Unable to construct ticket schema for ticket ${ticket.id}`
        )
        console.error(
          `Unable to construct ticket schema for ticket ${ticket.id}`
        )
        return undefined
      }
      return result
    },
    [workflowIdToDefaultStepId, errorNotification]
  )

  const doUpdateTicket = useCallback(
    async ({
      ticketId,
      stepId,
      fieldValues,
      workflowComplete,
    }: UpdateTicketArgs) => {
      const ticket = ticketEntries?.find((entry) => entry.id === ticketId)
      if (!ticket) {
        console.warn(`Unknown ticket id: ${ticketId}`)
        return
      }
      const ticketSchema = getTicketSchema(ticket)
      if (!ticketSchema) {
        return
      }
      const ticketData = ticketSchema.parse(ticket.data)
      const {
        fieldValues: ticketFieldValues,
        stepId: ticketStepId,
        workflowComplete: ticketWorkflowComplete,
      } = ticketData

      const patchFieldValues = fieldValues
        ? createPatchData(
            ticketId,
            { fieldValues: ticketFieldValues },
            { fieldValues }
          )
        : undefined

      const patchStepId = stepId
        ? createPatchData(ticketId, { stepId: ticketStepId }, { stepId })
        : undefined

      const patchWorkflowComplete = !isNil(workflowComplete)
        ? createPatchData(
            ticketId,
            { workflowComplete: ticketWorkflowComplete },
            { workflowComplete }
          )
        : undefined

      if (!patchFieldValues && !patchStepId && !patchWorkflowComplete) {
        console.warn(`No changes to ticket ${ticketId}`)
        return
      }
      await rolodexPatch(
        [patchFieldValues, patchStepId, patchWorkflowComplete].filter(
          (patch): patch is RolodexEntryPatchJson => patch !== undefined
        )
      )
      await forceRefresh()
    },
    [ticketEntries, getTicketSchema, rolodexPatch, forceRefresh]
  )
  return useMemo(() => {
    const sortedTickets =
      ticketEntries
        .map((entry) => {
          const schema = getTicketSchema(entry)
          if (!schema) return undefined
          try {
            return mapEntryToLibraTicket(entry, (data) => schema.parse(data))
          } catch (error) {
            console.error(
              `Failed to parse ticket data for entry ${entry.id}:`,
              error,
              entry
            )
            return undefined
          }
        })
        .filter((ticket): ticket is LibraTicket => ticket !== undefined)
        .toSorted((a, b) => {
          if (a.workflowComplete !== b.workflowComplete) {
            return a.workflowComplete ? 1 : -1
          }
          return b.createdAt.localeCompare(a.createdAt)
        }) ?? []

    const groups = groupEntries.map((entry) =>
      mapEntryToLibraTicketGroup(entry, (data) =>
        libraTicketGroupDataSchema.parse(data)
      )
    )

    const groupsMap = new Map(groups.map((group) => [group.id, group]))

    return {
      tickets: sortedTickets,
      ticketGroups: groupsMap,
      groups,
      isLoading,
      hasEverLoaded,
      forceRefresh,
      doUpdateTicket,
    }
  }, [
    doUpdateTicket,
    forceRefresh,
    getTicketSchema,
    groupEntries,
    hasEverLoaded,
    isLoading,
    ticketEntries,
  ])
}

export function useLibraWorkflows({
  ids,
  tags,
}: {
  ids?: ReadonlyArray<string>
  tags?: RolodexEntryTagsType
}): {
  libraWorkflows: ReadonlyArray<LibraWorkflow>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
} {
  const queryBody = useMemo<RolodexEntryQueryBody | undefined>(() => {
    const hasTags = tags && !isEmpty(tags)
    const hasIds = ids && !isEmpty(ids)
    if (!hasTags && !hasIds) {
      return undefined
    }

    return {
      type: LIBRA_WORKFLOW_ENTRY_TYPE,
      ...(hasTags ? { tags } : {}),
      ...(hasIds ? { ids: [...ids] } : {}),
    }
  }, [ids, tags])

  const {
    entries: libraWorkflows,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(queryBody)

  return useMemo(
    () => ({
      libraWorkflows: (libraWorkflows ?? []).map(mapEntryToLibraWorkflow),
      isLoading,
      hasEverLoaded,
      forceRefresh,
    }),
    [forceRefresh, hasEverLoaded, isLoading, libraWorkflows]
  )
}

export function useLibraGroups({
  ids,
  tags,
}: {
  ids?: ReadonlyArray<string>
  tags?: RolodexEntryTagsType | Record<string, string[]>
}): {
  groups: ReadonlyArray<LibraTicketGroup>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
} {
  const queryBody = useMemo<RolodexEntryQueryBody | undefined>(() => {
    const hasTags = tags && !isEmpty(tags)
    const hasIds = ids && !isEmpty(ids)
    if (!hasTags && !hasIds) {
      return undefined
    }

    return {
      type: LIBRA_TICKET_GROUP_ENTRY_TYPE,
      ...(hasTags ? { tags } : {}),
      ...(hasIds ? { ids: [...ids] } : {}),
    }
  }, [ids, tags])

  const { entries, isLoading, hasEverLoaded, forceRefresh } =
    useRolodexEntries(queryBody)

  return useMemo(
    () => ({
      groups: (entries ?? []).map((entry) =>
        mapEntryToLibraTicketGroup(entry, (data) =>
          libraTicketGroupDataSchema.parse(data)
        )
      ),
      isLoading,
      hasEverLoaded,
      forceRefresh,
    }),
    [entries, forceRefresh, hasEverLoaded, isLoading]
  )
}
